import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Bell, Gauge, KeyRound, Plug, Radio, SlidersHorizontal, Sparkles, Users } from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Skeleton } from "@/components/ui/misc";
import { ApiKeysSection } from "@/features/settings/api-keys";
import { EnrichmentSection } from "@/features/settings/enrichment";
import { GeneralSection } from "@/features/settings/general";
import { IngestionSection } from "@/features/settings/ingestion";
import { ConnectorsSection } from "@/features/settings/connectors";
import { IntegrationsSection } from "@/features/settings/integrations";
import { MembersSection } from "@/features/settings/team";
import { NotificationsSection } from "@/features/settings/notifications";
import { UsageSection } from "@/features/settings/usage";
import { q } from "@/lib/api";
import { cn } from "@/lib/utils";

const TABS = ["general", "enrichment", "api-keys", "ingestion", "integrations", "members", "notifications", "usage"] as const;
type SettingsTab = (typeof TABS)[number];

const TAB_META: Record<SettingsTab, { label: string; icon: ReactNode; description: string }> = {
  general: { label: "General", icon: <SlidersHorizontal />, description: "Privacy mode, data residency and retention." },
  enrichment: { label: "Analytics enrichment", icon: <Sparkles />, description: "Decision provider, generative sampling, budget, session timeout and content retention for product analytics." },
  "api-keys": { label: "API keys", icon: <KeyRound />, description: "Credentials for ingestion and the read API." },
  ingestion: { label: "Ingestion", icon: <Radio />, description: "Connect in-house agents with OpenTelemetry or the SDKs." },
  integrations: { label: "Integrations", icon: <Plug />, description: "Agent platforms, frameworks, payment events and alerting." },
  members: { label: "Members", icon: <Users />, description: "Who can access this workspace — your team, your agent platform's FDEs and your carrier — and pending invitations." },
  notifications: { label: "Notifications", icon: <Bell />, description: "Slack, email and webhook channels for issues, coverage and analytics events." },
  usage: { label: "Usage", icon: <Gauge />, description: "Metered usage this month against your plan, and enrichment spend." },
};

export const Route = createFileRoute("/_app/settings")({
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } => {
    const t = search.tab;
    return typeof t === "string" && (TABS as readonly string[]).includes(t) ? { tab: t as SettingsTab } : {};
  },
  component: SettingsPage,
});

function SettingsPage() {
  const { tab = "general" } = Route.useSearch();
  const query = useQuery(q.settings());
  const org = useQuery(q.org()).data;
  const meta = TAB_META[tab];

  return (
    <Page>
      <PageHeader title="Settings" description={`Workspace configuration for ${org?.name ?? "your organization"}.`} />
      <div className="grid gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav aria-label="Settings sections" className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
          {TABS.map((t) => (
            <Link
              key={t}
              to="/settings"
              search={t === "general" ? {} : { tab: t }}
              aria-current={t === tab ? "page" : undefined}
              className={cn(
                "flex h-8 shrink-0 items-center gap-2 rounded-md px-2.5 text-[13px] font-medium transition-colors [&_svg]:size-4",
                t === tab ? "bg-surface-2 text-fg [&_svg]:text-accent" : "text-fg-3 hover:bg-surface-2/60 hover:text-fg",
              )}
            >
              {TAB_META[t].icon}
              {TAB_META[t].label}
            </Link>
          ))}
        </nav>
        <section aria-labelledby="settings-section-title" className="min-w-0">
          <div className="mb-4">
            <h2 id="settings-section-title" className="text-[15px] font-semibold">
              {meta.label}
            </h2>
            <p className="text-xs text-fg-3">{meta.description}</p>
          </div>
          <QueryState
            query={query}
            loading={
              <div className="flex flex-col gap-3">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-28 w-full" />
              </div>
            }
          >
            {(s) => (
              <>
                {tab === "general" ? <GeneralSection settings={s} /> : null}
                {tab === "enrichment" ? <EnrichmentSection settings={s} /> : null}
                {tab === "api-keys" ? <ApiKeysSection /> : null}
                {tab === "ingestion" ? <IngestionSection settings={s} /> : null}
                {tab === "integrations" ? (
                  <div className="flex flex-col gap-6">
                    <IntegrationsSection integrations={s.integrations} />
                    <ConnectorsSection />
                  </div>
                ) : null}
                {tab === "members" ? <MembersSection /> : null}
                {tab === "notifications" ? <NotificationsSection settings={s} /> : null}
                {tab === "usage" ? <UsageSection settings={s} /> : null}
              </>
            )}
          </QueryState>
        </section>
      </div>
    </Page>
  );
}
